import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Car, 
  MapPin, 
  Calendar, 
  User, 
  CreditCard,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Gauge,
  Trash2,
  XCircle,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  TrafficFine, 
  FineAction,
  OFFENSE_TYPE_LABELS, 
  FINE_STATUS_LABELS,
  getDaysRemaining,
  getCurrentAmount,
  useTrafficFines
} from '@/hooks/useTrafficFines';
import { DriverDesignationDialog } from './DriverDesignationDialog';
import { useDocumentPreview } from '@/contexts/DocumentPreviewContext';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import type { Tables } from '@/integrations/supabase/types';

interface FineDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fine: TrafficFine | null;
}

export function FineDetailSheet({ open, onOpenChange, fine }: FineDetailSheetProps) {
  const { markAsPaid, contestFine, deleteFine, getFineActions } = useTrafficFines();
  const { openDocument } = useDocumentPreview();
  const [showDesignationDialog, setShowDesignationDialog] = useState(false);
  const [isEditingDriver, setIsEditingDriver] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [paymentReference, setPaymentReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch the associated document if exists
  const { data: fineDocument } = useQuery({
    queryKey: ['fine-document', fine?.document_id],
    queryFn: async () => {
      if (!fine?.document_id) return null;
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('id', fine.document_id)
        .single();
      if (error) return null;
      return data as Tables<"documents">;
    },
    enabled: !!fine?.document_id && open,
  });

  const actionsQuery = useQuery({
    queryKey: ['fine-actions', fine?.id],
    queryFn: async () => {
      if (!fine?.id) return [] as FineAction[];
      return await getFineActions(fine.id);
    },
    enabled: !!fine?.id && open,
  });

  const actions = actionsQuery.data ?? [];
  const isLoadingActions = actionsQuery.isLoading;
  const actionsError = actionsQuery.error;

  if (!fine) return null;

  const daysRemaining = getDaysRemaining(fine.designation_deadline);
  const currentAmount = getCurrentAmount(fine);
  const isPending = fine.status === 'received' || fine.status === 'pending_designation';

  const handleMarkAsPaid = async () => {
    setIsSubmitting(true);
    try {
      await markAsPaid.mutateAsync({ fineId: fine.id, paymentReference });
      setShowPaymentDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    try {
      await deleteFine.mutateAsync(fine.id);
      setShowDeleteDialog(false);
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'created': return FileText;
      case 'designated': return User;
      case 'paid': return CreditCard;
      case 'contested': return AlertTriangle;
      default: return Clock;
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>{OFFENSE_TYPE_LABELS[fine.offense_type]}</span>
              <Badge className={cn(
                fine.status === 'paid' && "bg-green-100 text-green-800",
                fine.status === 'contested' && "bg-purple-100 text-purple-800",
                isPending && "bg-amber-100 text-amber-800"
              )}>
                {FINE_STATUS_LABELS[fine.status]}
              </Badge>
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-6 mt-6">
            {/* Main info card */}
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* License plate */}
                <div className="flex items-center gap-3">
                  <Car className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-mono font-semibold text-lg">{fine.license_plate}</p>
                    {fine.vehicle && (
                      <p className="text-sm text-muted-foreground">
                        {fine.vehicle.brand} {fine.vehicle.model}
                      </p>
                    )}
                  </div>
                </div>

                {/* Date & location */}
                {fine.offense_date && (
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <span>
                      {format(new Date(fine.offense_date), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
                    </span>
                  </div>
                )}

                {fine.offense_location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground shrink-0" />
                    <span>{fine.offense_location}</span>
                  </div>
                )}

                {/* Speed info */}
                {fine.speed_retained && fine.speed_limit && (
                  <div className="flex items-center gap-3">
                    <Gauge className="h-5 w-5 text-muted-foreground" />
                    <span>
                      <span className="text-destructive font-semibold">{fine.speed_retained} km/h</span>
                      {' '}au lieu de{' '}
                      <span className="font-semibold">{fine.speed_limit} km/h</span>
                    </span>
                  </div>
                )}

                {/* Reference */}
                {fine.reference_number && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="font-mono text-sm">{fine.reference_number}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Amounts */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Montants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Montant actuel</span>
                  <span className="text-2xl font-bold">{currentAmount.toFixed(2)} €</span>
                </div>
                {fine.reduced_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Minoré</span>
                    <span className={cn(
                      currentAmount === fine.reduced_amount && "text-green-600 font-medium"
                    )}>
                      {fine.reduced_amount.toFixed(2)} €
                    </span>
                  </div>
                )}
                {fine.fine_amount && fine.fine_amount !== fine.reduced_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Forfaitaire</span>
                    <span>{fine.fine_amount.toFixed(2)} €</span>
                  </div>
                )}
                {fine.increased_amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Majoré</span>
                    <span className="text-destructive">{fine.increased_amount.toFixed(2)} €</span>
                  </div>
                )}
                {fine.points_withdrawn && fine.points_withdrawn > 0 && (
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-muted-foreground">Points retirés</span>
                    <span className="text-destructive font-medium">-{fine.points_withdrawn} pts</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Document preview */}
            {fineDocument && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Document associé
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={() => openDocument(fineDocument)}
                  >
                    <Eye className="h-4 w-4" />
                    Voir l'avis d'amende
                  </Button>
                  {/* Download CERFA button for designated fines */}
                  {fine.driver && (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={async () => {
                        try {
                          const { data, error } = await supabase.functions.invoke('fill-designation-form', {
                            body: { fineId: fine.id, driverId: fine.driver_id }
                          });

                          if (error) throw error;

                          if (data?.pdf) {
                            const byteCharacters = atob(data.pdf);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                              byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: 'application/pdf' });
                            const url = URL.createObjectURL(blob);
                            
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = data.filename || 'designation.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }
                        } catch (err) {
                          console.error('PDF download error:', err);
                        }
                      }}
                    >
                      <FileText className="h-4 w-4" />
                      Télécharger le formulaire CERFA
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Designation section - show for pending OR when driver is designated */}
            {(isPending || fine.driver) && (
              <Card className={cn(
                isPending && daysRemaining !== null && daysRemaining <= 7 && "border-destructive/50"
              )}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {fine.driver ? 'Conducteur désigné' : 'Désignation du conducteur'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {isPending && !fine.driver && daysRemaining !== null && (
                    <div className={cn(
                      "flex items-center gap-2 p-2 rounded-lg",
                      daysRemaining <= 3 && "bg-destructive/10 text-destructive",
                      daysRemaining > 3 && daysRemaining <= 7 && "bg-amber-100 text-amber-800",
                      daysRemaining > 7 && "bg-muted"
                    )}>
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {daysRemaining > 0 
                          ? `${daysRemaining} jour${daysRemaining > 1 ? 's' : ''} restant${daysRemaining > 1 ? 's' : ''}`
                          : daysRemaining === 0 
                            ? "Dernier jour !"
                            : "Délai dépassé"
                        }
                      </span>
                    </div>
                  )}

                  {fine.driver ? (
                    <div className="flex items-center justify-between gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="font-medium">{fine.driver.first_name} {fine.driver.last_name}</p>
                          <p className="text-sm text-muted-foreground">{fine.driver.email}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsEditingDriver(true);
                          setShowDesignationDialog(true);
                        }}
                      >
                        Changer
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      onClick={() => {
                        setIsEditingDriver(false);
                        setShowDesignationDialog(true);
                      }}
                      variant={daysRemaining !== null && daysRemaining <= 7 ? "destructive" : "default"}
                      className="w-full"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Désigner le conducteur
                    </Button>
                  )}

                  {isPending && !fine.driver && (
                    <p className="text-xs text-muted-foreground">
                      En cas de non-désignation dans les 45 jours, l'entreprise risque une amende de 750€.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Historique</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingActions ? (
                  <Spinner className="h-5 w-5 mx-auto my-4" />
                ) : actionsError ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Impossible de charger l’historique.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => actionsQuery.refetch()}
                    >
                      Réessayer
                    </Button>
                  </div>
                ) : actions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Aucun historique.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {actions.map((action, index) => {
                      const Icon = getActionIcon(action.action_type);
                      return (
                        <div key={action.id} className="flex gap-3">
                          <div
                            className={cn(
                              "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                              index === actions.length - 1 ? "bg-primary/10" : "bg-muted"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">{action.notes || action.action_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(action.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-2 pb-4">
              {fine.status !== 'paid' && fine.status !== 'cancelled' && (
                <Button 
                  onClick={() => setShowPaymentDialog(true)}
                  className="w-full"
                  variant="default"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Marquer comme payée
                </Button>
              )}

              <Button 
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                className="w-full text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Designation dialog */}
      <DriverDesignationDialog
        open={showDesignationDialog}
        onOpenChange={(open) => {
          setShowDesignationDialog(open);
          if (!open) setIsEditingDriver(false);
        }}
        fine={fine}
        isEditing={isEditingDriver}
      />

      {/* Payment dialog */}
      <AlertDialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marquer comme payée</AlertDialogTitle>
            <AlertDialogDescription>
              Confirmez le paiement de {currentAmount.toFixed(2)} € pour cette amende.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="payment-ref">Référence de paiement (optionnel)</Label>
            <Input
              id="payment-ref"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              placeholder="Ex: Télépaiement du 15/01"
              className="mt-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkAsPaid} disabled={isSubmitting}>
              {isSubmitting ? <Spinner className="h-4 w-4 mr-2" /> : null}
              Confirmer le paiement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette amende ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. L'amende sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isSubmitting ? <Spinner className="h-4 w-4 mr-2" /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
